import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { IShareSelection, TChangeSelectionHandler } from 'APP/types/share';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

export interface IForwardMessageQuotePopupProps {
  message: ChatMessage;
  text: string;
}

export interface IForwardMessageQuotePopupPresenter {
  message: ChatMessage | null;
  text: string | null;
  group: Group | null;
  isAvailableToShow: boolean;

  setMessage(message: ChatMessage): void;
  setText(text: string): void;
  onClose(): void;
  onSelectGroup(group: Group): Promise<void>;
  onGroupClick: TChangeSelectionHandler;
}

export function useForwardMessageQuotePresenter({
  params,
  popupInstance,
}: ICommonPopupsProps<IForwardMessageQuotePopupProps>): IForwardMessageQuotePopupPresenter {
  const presenter = useLocalObservable<IForwardMessageQuotePopupPresenter>(() => ({
    message: null,
    text: null,

    get group(): Group | null {
      const groupId = presenter.message?.groupId;
      if (!groupId) {
        return null;
      }
      return Entities.GroupsStore.getGroupById(groupId);
    },

    get isAvailableToShow(): boolean {
      return Boolean(presenter.group);
    },

    setMessage(message: ChatMessage): void {
      presenter.message = message;
    },

    setText(text: string): void {
      presenter.text = text;
    },

    onClose(): void {
      popupInstance.close();
    },

    async onSelectGroup(group: Group): Promise<void> {
      if (!presenter.message || !presenter.text) {
        return;
      }

      group.messagesStore.setSavePosition({ isBottom: true });
      await Tasks.group.setActiveGroup({ groupId: group.id });
      await Tasks.messaging.startQuote(presenter.message, presenter.text);
      presenter.onClose();
    },

    onGroupClick(data: IShareSelection[]): void {
      const selection = data[0];
      const groupId = selection.data.id;
      const targetGroup = Entities.GroupsStore.getGroupById(groupId);

      if (!targetGroup) {
        return;
      }

      presenter.onSelectGroup(targetGroup);
    },
  }));

  useEffect(() => {
    if (!params) {
      return;
    }
    presenter.setMessage(params.message);
    presenter.setText(params.text);
  }, [params]);

  return presenter;
}
